import React, { useState, useEffect, useRef } from 'react';
import { 
    Plus, Search, Printer, QrCode, ShieldCheck, AlertTriangle, 
    UserCheck, Users, Phone, Calendar, X, FileText, CheckCircle, 
    RefreshCw, Award, Sparkles, ChevronRight, Download, RotateCw, CreditCard, DollarSign
} from 'lucide-react';
import api from '../services/api';
import ReceiptModal from '../components/ReceiptModal';

const DEFAULT_TKD_FORFAITS = [
    { id: 12, name: 'TAEKWONDO ENFANT', price: 15000, registration_fee: 10000, duration: '1 Mois', periodicity: 'mensuel' }
];

interface PickupPerson {
    id?: number;
    card_number?: string;
    first_name: string;
    last_name: string;
    relationship: string;
    phone: string;
    photo_url?: string;
    status?: 'actif' | 'desactive';
}

interface TaekwondoStudent {
    id: number;
    card_number: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    level: string;
    photo_url?: string;
    parent_name: string;
    parent_phone: string;
    parent_email?: string;
    photo_authorization: boolean;
    status: 'actif' | 'inactif' | 'suspendu' | 'carte_perdue';
    created_at: string;
    pickups: PickupPerson[];
    subscription_id?: number;
    forfait_id?: number;
    forfait_name?: string;
    subscription_start_date?: string;
    subscription_end_date?: string;
    subscription_amount?: number | string;
    subscription_registration_fee?: number | string;
    subscription_total_paid?: number | string;
    subscription_payment_method?: string;
    subscription_status?: string;
    computed_subscription_status?: 'actif' | 'expire' | 'aucun';
}

const TaekwondoKids = () => {
    const [students, setStudents] = useState<TaekwondoStudent[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'actif' | 'carte_perdue'>('all');

    // Modales
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [viewingCardsStudent, setViewingCardsStudent] = useState<TaekwondoStudent | null>(null);
    const [viewingContractStudent, setViewingContractStudent] = useState<TaekwondoStudent | null>(null);

    // Formulaire Inscription
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        level: 'DÉBUTANT',
        photo_url: '',
        parent_name: '',
        parent_phone: '',
        parent_email: '',
        photo_authorization: true
    });

    const [pickupsData, setPickupsData] = useState<PickupPerson[]>([
        { first_name: '', last_name: '', relationship: 'MÈRE', phone: '', photo_url: '' }
    ]);

    // Forfaits & Abonnements Taekwondo
    const [forfaits, setForfaits] = useState<any[]>(DEFAULT_TKD_FORFAITS);
    const [selectedForfaitId, setSelectedForfaitId] = useState<number>(12);
    const [includeRegFee, setIncludeRegFee] = useState(true);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentMethod, setPaymentMethod] = useState('especes');

    // Réabonnement Modal State
    const [reaboStudent, setReaboStudent] = useState<TaekwondoStudent | null>(null);
    const [reaboForfaitId, setReaboForfaitId] = useState<number>(12);
    const [reaboIncludeRegFee, setReaboIncludeRegFee] = useState(false);
    const [reaboStartDate, setReaboStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [reaboPaymentMethod, setReaboPaymentMethod] = useState('especes');
    const [reaboSubmitting, setReaboSubmitting] = useState(false);

    // Reçu imprimable
    const [receiptData, setReceiptData] = useState<any>(null);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const res = await api.get('/taekwondo/students');
            setStudents(res.data);
        } catch (err) {
            console.error('Erreur chargement élèves taekwondo:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchForfaits = async () => {
        try {
            const res = await api.get('/forfaits/abonnements');
            const all = res.data || [];
            const tkd = all.filter((f: any) => 
                f.name.toLowerCase().includes('taekwondo') || f.description?.toLowerCase().includes('taekwondo')
            );
            const list = tkd.length > 0 ? tkd : (all.length > 0 ? all : DEFAULT_TKD_FORFAITS);
            setForfaits(list);
            if (list.length > 0) {
                const def = list.find((f: any) => f.name.toUpperCase().includes('TAEKWONDO ENFANT')) || list[0];
                setSelectedForfaitId(def.id);
                setReaboForfaitId(def.id);
            }
        } catch (err) {
            console.error('Erreur chargement forfaits taekwondo:', err);
            setForfaits(DEFAULT_TKD_FORFAITS);
        }
    };

    useEffect(() => {
        fetchStudents();
        fetchForfaits();
    }, []);

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            date_of_birth: '',
            level: 'DÉBUTANT',
            photo_url: '',
            parent_name: '',
            parent_phone: '',
            parent_email: '',
            photo_authorization: true
        });
        setPickupsData([
            { first_name: '', last_name: '', relationship: 'MÈRE', phone: '', photo_url: '' }
        ]);
    };

    const handleAddPickupField = () => {
        setPickupsData([
            ...pickupsData,
            { first_name: '', last_name: '', relationship: 'AUTRE', phone: '', photo_url: '' }
        ]);
    };

    const handleRemovePickupField = (index: number) => {
        if (pickupsData.length > 1) {
            setPickupsData(pickupsData.filter((_, i) => i !== index));
        }
    };

    const handlePickupChange = (index: number, field: keyof PickupPerson, value: string) => {
        const updated = [...pickupsData];
        updated[index] = { ...updated[index], [field]: value };
        setPickupsData(updated);
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>, isStudent: boolean, pickupIndex?: number) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result as string;
                if (isStudent) {
                    setFormData(prev => ({ ...prev, photo_url: base64 }));
                } else if (pickupIndex !== undefined) {
                    const updated = [...pickupsData];
                    updated[pickupIndex].photo_url = base64;
                    setPickupsData(updated);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const currentForfait = forfaits.find(f => f.id === selectedForfaitId) || forfaits[0] || DEFAULT_TKD_FORFAITS[0];
    const basePrice = currentForfait ? parseFloat(currentForfait.price) : 15000;
    const regFee = includeRegFee ? (currentForfait ? parseFloat(currentForfait.registration_fee ?? 10000) : 10000) : 0;
    const totalAmount = basePrice + regFee;

    const handleSubmitRegistration = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            const payload = {
                ...formData,
                pickups: pickupsData.filter(p => p.first_name.trim() !== '' && p.last_name.trim() !== ''),
                forfait_id: selectedForfaitId,
                include_registration_fee: includeRegFee,
                start_date: startDate,
                payment_method: paymentMethod
            };

            const res = await api.post('/taekwondo/students', payload);
            setIsCreateModalOpen(false);
            resetForm();
            fetchStudents();

            // Reçu imprimable
            setReceiptData({
                planName: res.data.subscription?.forfait_name || currentForfait?.name || 'TAEKWONDO ENFANT',
                client: {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    phone: formData.parent_phone
                },
                amount: totalAmount,
                basePrice: basePrice,
                registrationFee: regFee,
                paymentMethod: paymentMethod,
                startDate: startDate,
                endDate: res.data.subscription?.end_date,
                duration: currentForfait?.duration || '1 Mois'
            });
            setIsReceiptOpen(true);
        } catch (err: any) {
            console.error('Erreur inscription:', err);
            alert(err.response?.data?.error || 'Erreur lors de l\'inscription');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenReabo = (student: TaekwondoStudent) => {
        setReaboStudent(student);
        setReaboIncludeRegFee(false);
        setReaboStartDate(new Date().toISOString().split('T')[0]);
        setReaboPaymentMethod('especes');
        if (student.forfait_id) {
            setReaboForfaitId(student.forfait_id);
        }
    };

    const handleSubmitReabo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reaboStudent) return;
        try {
            setReaboSubmitting(true);
            const currentF = forfaits.find(f => f.id === reaboForfaitId) || forfaits[0];
            const res = await api.post(`/taekwondo/students/${reaboStudent.id}/reabonnement`, {
                forfait_id: reaboForfaitId,
                include_registration_fee: reaboIncludeRegFee,
                start_date: reaboStartDate,
                payment_method: reaboPaymentMethod
            });

            setReaboStudent(null);
            fetchStudents();

            if (res.data.receiptData) {
                setReceiptData({
                    planName: res.data.receiptData.forfait_name,
                    client: {
                        first_name: reaboStudent.first_name,
                        last_name: reaboStudent.last_name,
                        phone: reaboStudent.parent_phone
                    },
                    amount: res.data.receiptData.total_amount,
                    basePrice: res.data.receiptData.base_price,
                    registrationFee: res.data.receiptData.registration_fee,
                    paymentMethod: res.data.receiptData.payment_method,
                    startDate: res.data.receiptData.start_date,
                    endDate: res.data.receiptData.end_date,
                    duration: currentF?.duration || '1 Mois'
                });
                setIsReceiptOpen(true);
            }
        } catch (err: any) {
            console.error('Erreur réabonnement:', err);
            alert(err.response?.data?.error || 'Erreur lors du réabonnement');
        } finally {
            setReaboSubmitting(false);
        }
    };

    const handleReportLostCard = async (student: TaekwondoStudent) => {
        const confirmMsg = `Confirmez-vous la déclaration de perte pour la carte de ${student.first_name} ${student.last_name} ?\n\nConformément au règlement, l'ancienne carte (${student.card_number}) sera désactivée et une nouvelle sera émise avec facturation de 10.000 F CFA.`;
        if (window.confirm(confirmMsg)) {
            try {
                const res = await api.post('/taekwondo/report-lost-card', { type: 'student', id: student.id });
                alert(res.data.message);
                fetchStudents();
            } catch (err: any) {
                alert(err.response?.data?.error || 'Erreur');
            }
        }
    };

    const filteredStudents = students.filter(s => {
        const matchesSearch = 
            `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.card_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.parent_name.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (statusFilter === 'all') return matchesSearch;
        return matchesSearch && s.status === statusFilter;
    });

    const handlePrintWindow = () => {
        window.print();
    };

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-yellow-600 mb-1">
                        <Award size={16} /> Section Arts Martiaux Juniors
                    </div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                        Taekwondo Enfants — Cartes & Sécurité
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Gestion des inscriptions, génération automatique des badges avec code QR et autorisations de sortie.
                    </p>
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setIsCreateModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-5 py-3 bg-[#eab308] hover:bg-yellow-400 text-black font-extrabold rounded-xl transition-all shadow-md hover:shadow-lg cursor-pointer"
                >
                    <Plus size={20} />
                    <span>Nouvelle Inscription Taekwondo</span>
                </button>
            </div>

            {/* Barre de Recherche & Filtres */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3.5 top-3 text-gray-400" size={18} />
                    <input 
                        type="text"
                        placeholder="Rechercher élève, carte TKD, parent..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#eab308]"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-xs font-bold text-gray-500 uppercase">Statut :</span>
                    <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                            statusFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        Tous ({students.length})
                    </button>
                    <button
                        onClick={() => setStatusFilter('actif')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                            statusFilter === 'actif' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                    >
                        Actifs ({students.filter(s => s.status === 'actif').length})
                    </button>
                    <button
                        onClick={() => setStatusFilter('carte_perdue')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                            statusFilter === 'carte_perdue' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                    >
                        Cartes Perdues ({students.filter(s => s.status === 'carte_perdue').length})
                    </button>
                </div>
            </div>

            {/* Liste des Inscrits */}
            {loading ? (
                <div className="bg-white p-12 text-center rounded-2xl border border-gray-100 text-gray-400">
                    Chargement des élèves de Taekwondo...
                </div>
            ) : filteredStudents.length === 0 ? (
                <div className="bg-white p-12 text-center rounded-2xl border border-gray-100">
                    <ShieldCheck size={48} className="mx-auto text-gray-300 mb-3" />
                    <h3 className="text-lg font-bold text-gray-700">Aucun élève trouvé</h3>
                    <p className="text-sm text-gray-500 mt-1">Cliquez sur « Nouvelle Inscription Taekwondo » pour enregistrer un premier élève.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredStudents.map((student) => (
                        <div 
                            key={student.id} 
                            className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col justify-between"
                        >
                            <div>
                                {/* Top Banner Badge */}
                                <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono font-black text-sm px-2.5 py-1 rounded-md bg-yellow-100 text-yellow-900 border border-yellow-300">
                                            {student.card_number}
                                        </span>
                                        <span className="text-xs font-extrabold px-2.5 py-1 rounded-md bg-black text-yellow-400">
                                            {student.level}
                                        </span>
                                    </div>
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                        student.status === 'actif' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {student.status.toUpperCase()}
                                    </span>
                                </div>

                                {/* Profil de l'enfant */}
                                <div className="flex items-start gap-4 mb-5">
                                    <div className="w-20 h-24 rounded-xl bg-gray-100 border-2 border-[#eab308] overflow-hidden shrink-0 flex items-center justify-center">
                                        {student.photo_url ? (
                                            <img src={student.photo_url} alt={student.first_name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="text-center p-2 text-gray-400">
                                                <Award size={24} className="mx-auto text-yellow-500 mb-1" />
                                                <span className="text-[10px] font-bold block">PHOTO</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-black text-gray-900">
                                            {student.first_name} {student.last_name}
                                        </h3>
                                        <div className="text-xs text-gray-500 space-y-1 mt-1">
                                            <p className="flex items-center gap-1.5">
                                                <Calendar size={13} className="text-gray-400" />
                                                <span>Né(e) le : <strong>{new Date(student.date_of_birth).toLocaleDateString('fr-FR')}</strong></span>
                                            </p>
                                            <p className="flex items-center gap-1.5">
                                                <Users size={13} className="text-gray-400" />
                                                <span>Parent / Tuteur : <strong>{student.parent_name}</strong></span>
                                            </p>
                                            <p className="flex items-center gap-1.5">
                                                <Phone size={13} className="text-gray-400" />
                                                <span>Urgence : <strong>{student.parent_phone}</strong></span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Personnes Autorisées à récupérer l'enfant */}
                                <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100 mb-4">
                                    <h4 className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <UserCheck size={14} className="text-green-600" />
                                        Personnes Autorisées à Récupérer ({student.pickups?.length || 0}) :
                                    </h4>
                                    {student.pickups && student.pickups.length > 0 ? (
                                        <div className="space-y-2">
                                            {student.pickups.map((p, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs bg-white p-2 rounded-lg border border-gray-200">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                                                            {p.card_number || 'PT-...'}
                                                        </span>
                                                        <span className="font-bold text-gray-900">{p.first_name} {p.last_name}</span>
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">
                                                            {p.relationship}
                                                        </span>
                                                    </div>
                                                    <span className="text-gray-500 font-medium">{p.phone}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-red-500 italic">
                                            ⚠️ Aucune personne autorisée déclarée !
                                        </p>
                                    )}
                                </div>

                                {/* Encart Abonnement & Cotisation Taekwondo */}
                                <div className="p-3 bg-amber-50/70 border border-amber-200/90 rounded-xl flex items-center justify-between text-xs mb-4">
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-black text-gray-900">
                                                🥋 {student.forfait_name || 'TAEKWONDO ENFANT'}
                                            </span>
                                            {student.subscription_total_paid && (
                                                <span className="text-[10px] font-bold text-amber-800 bg-amber-200/60 px-1.5 py-0.2 rounded">
                                                    {Number(student.subscription_total_paid).toLocaleString('fr-FR')} F
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-gray-600 block mt-0.5">
                                            {student.subscription_end_date ? (
                                                <>Échéance : <strong>{new Date(student.subscription_end_date).toLocaleDateString('fr-FR')}</strong></>
                                            ) : (
                                                <span className="text-amber-800 font-medium">Cotisation à régulariser</span>
                                            )}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {student.computed_subscription_status === 'actif' ? (
                                            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-extrabold rounded-full text-[11px]">
                                                Abonnement Actif
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleOpenReabo(student)}
                                                className="px-3 py-1.5 bg-[#eab308] hover:bg-yellow-400 text-black font-black rounded-lg text-xs flex items-center gap-1 transition-all shadow-xs cursor-pointer"
                                                title="Renouveler l'abonnement Taekwondo"
                                            >
                                                <RotateCw size={13} />
                                                <span>Réabonner</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Actions rapides */}
                            <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setViewingCardsStudent(student)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-black hover:bg-gray-800 text-yellow-400 font-bold text-xs rounded-lg transition-colors cursor-pointer"
                                        title="Voir et imprimer les cartes Enfant et Parent avec QR code"
                                    >
                                        <QrCode size={14} />
                                        <span>Générer les Cartes</span>
                                    </button>
                                    <button
                                        onClick={() => setViewingContractStudent(student)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs rounded-lg transition-colors cursor-pointer"
                                        title="Voir et imprimer la fiche d'inscription officielle"
                                    >
                                        <FileText size={14} />
                                        <span>Fiche d'Inscription (A4)</span>
                                    </button>
                                </div>

                                <button
                                    onClick={() => handleReportLostCard(student)}
                                    className="text-xs text-red-600 hover:text-red-700 font-medium hover:underline flex items-center gap-1 cursor-pointer"
                                    title="Désactiver et émettre une nouvelle carte (10.000 F CFA)"
                                >
                                    <AlertTriangle size={13} />
                                    <span>Perte de Carte (10.000 F)</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ======================================================== */}
            {/* MODALE 1 : NOUVELLE INSCRIPTION TAEKWONDO ENFANT          */}
            {/* ======================================================== */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-gray-100 p-6 md:p-8">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
                            <div>
                                <span className="text-xs font-black uppercase tracking-wider text-yellow-600 block">
                                    Dossier d'Adhésion
                                </span>
                                <h2 className="text-2xl font-black text-gray-900">
                                    Inscription Taekwondo Enfants
                                </h2>
                            </div>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitRegistration} className="space-y-6">
                            {/* Section 1 : Informations Enfant */}
                            <div className="bg-yellow-50/50 p-5 rounded-2xl border border-yellow-200/60">
                                <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                                    <Award size={16} className="text-yellow-600" />
                                    1. Informations de l'Enfant
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Nom de l'enfant *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Ex: KOUASSI"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.last_name}
                                            onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Prénoms *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Ex: Jeremy"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.first_name}
                                            onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Date de naissance *</label>
                                        <input
                                            type="date"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.date_of_birth}
                                            onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Niveau / Grade</label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.level}
                                            onChange={e => setFormData({ ...formData, level: e.target.value })}
                                        >
                                            <option value="DÉBUTANT">DÉBUTANT</option>
                                            <option value="CEINTURE BLANCHE">CEINTURE BLANCHE</option>
                                            <option value="CEINTURE JAUNE">CEINTURE JAUNE</option>
                                            <option value="CEINTURE ORANGE">CEINTURE ORANGE</option>
                                            <option value="CEINTURE VERTE">CEINTURE VERTE</option>
                                            <option value="CEINTURE BLEUE">CEINTURE BLEUE</option>
                                            <option value="CEINTURE ROUGE">CEINTURE ROUGE</option>
                                            <option value="CEINTURE NOIRE">CEINTURE NOIRE</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Photo d'identité de l'enfant (Obligatoire pour la carte)</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={e => handlePhotoUpload(e, true)}
                                            className="w-full text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-yellow-100 file:text-yellow-800 hover:file:bg-yellow-200"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2 : Responsable Légal */}
                            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-200">
                                <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                                    <Users size={16} className="text-gray-700" />
                                    2. Responsable Légal (Parent / Tuteur)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Nom & Prénoms du Parent *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Ex: KOUASSI Aman Nathalie"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.parent_name}
                                            onChange={e => setFormData({ ...formData, parent_name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Téléphone d'urgence *</label>
                                        <input
                                            type="tel"
                                            required
                                            placeholder="Ex: 07 13 47 07 99"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.parent_phone}
                                            onChange={e => setFormData({ ...formData, parent_phone: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Email</label>
                                        <input
                                            type="email"
                                            placeholder="parent@email.com"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#eab308]"
                                            value={formData.parent_email}
                                            onChange={e => setFormData({ ...formData, parent_email: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 3 : Personnes Autorisées à Récupérer l'Enfant */}
                            <div className="bg-blue-50/40 p-5 rounded-2xl border border-blue-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h3 className="text-sm font-extrabold text-blue-900 uppercase tracking-wide flex items-center gap-2">
                                            <ShieldCheck size={16} className="text-blue-700" />
                                            3. Personnes Autorisées à Récupérer l'Enfant
                                        </h3>
                                        <p className="text-xs text-blue-700 mt-0.5">
                                            Chaque personne autorisée recevra une carte badge avec QR code pour sécuriser la sortie.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleAddPickupField}
                                        className="text-xs font-bold px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 cursor-pointer"
                                    >
                                        <Plus size={14} /> Ajouter une personne
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {pickupsData.map((pickup, idx) => (
                                        <div key={idx} className="bg-white p-4 rounded-xl border border-blue-200 relative">
                                            {pickupsData.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePickupField(idx)}
                                                    className="absolute top-3 right-3 text-red-500 hover:text-red-700 text-xs font-bold"
                                                >
                                                    Supprimer
                                                </button>
                                            )}
                                            <span className="text-[11px] font-black uppercase text-blue-800 mb-2 block">
                                                Personne Autorisée #{idx + 1}
                                            </span>
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-gray-700 mb-1">Nom *</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="Nom"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs uppercase"
                                                        value={pickup.last_name}
                                                        onChange={e => handlePickupChange(idx, 'last_name', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-gray-700 mb-1">Prénoms *</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="Prénoms"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs"
                                                        value={pickup.first_name}
                                                        onChange={e => handlePickupChange(idx, 'first_name', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-gray-700 mb-1">Lien avec l'enfant *</label>
                                                    <select
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs"
                                                        value={pickup.relationship}
                                                        onChange={e => handlePickupChange(idx, 'relationship', e.target.value)}
                                                    >
                                                        <option value="MÈRE">MÈRE</option>
                                                        <option value="PÈRE">PÈRE</option>
                                                        <option value="TUTEUR">TUTEUR</option>
                                                        <option value="ONCLE">ONCLE</option>
                                                        <option value="TANTE">TANTE</option>
                                                        <option value="GRAND-PARENT">GRAND-PARENT</option>
                                                        <option value="CHAUFFEUR">CHAUFFEUR</option>
                                                        <option value="NOUNOU">NOUNOU</option>
                                                        <option value="AUTRE">AUTRE</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-gray-700 mb-1">Téléphone *</label>
                                                    <input
                                                        type="tel"
                                                        required
                                                        placeholder="Téléphone"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs"
                                                        value={pickup.phone}
                                                        onChange={e => handlePickupChange(idx, 'phone', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <label className="block text-[11px] font-bold text-gray-700 mb-1">Photo d'identification</label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={e => handlePhotoUpload(e, false, idx)}
                                                    className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-gray-100"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Section 4 : Formule, Abonnement & Paiement Taekwondo */}
                            <div className="bg-emerald-50/50 p-5 rounded-2xl border border-emerald-200/80 space-y-4">
                                <div className="border-b border-emerald-200/60 pb-3">
                                    <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                                        <CreditCard size={18} className="text-emerald-700" />
                                        4. Forfait, Cotisation & Paiement Taekwondo
                                    </h3>
                                    <p className="text-xs text-emerald-800 mt-0.5">
                                        L'adhésion au Taekwondo inclut la souscription au forfait et les frais d'inscription annuels.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Choix Forfait */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                            Forfait Taekwondo *
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#eab308]"
                                            value={selectedForfaitId}
                                            onChange={e => setSelectedForfaitId(Number(e.target.value))}
                                        >
                                            {forfaits.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name} — {Number(f.price).toLocaleString('fr-FR')} FCFA / {f.duration || f.periodicity}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Date début */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                            Date de début d'abonnement *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={startDate}
                                            onChange={e => setStartDate(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#eab308]"
                                        />
                                    </div>

                                    {/* Mode de paiement */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                            Mode de paiement *
                                        </label>
                                        <select
                                            value={paymentMethod}
                                            onChange={e => setPaymentMethod(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#eab308]"
                                        >
                                            <option value="especes">Espèces</option>
                                            <option value="wave">Wave</option>
                                            <option value="orange_money">Orange Money</option>
                                            <option value="mtn_money">MTN Money</option>
                                            <option value="moov_money">Moov Money</option>
                                            <option value="carte">Carte Bancaire</option>
                                            <option value="virement">Virement</option>
                                        </select>
                                    </div>

                                    {/* Frais d'inscription annuels */}
                                    <div className="flex flex-col justify-end">
                                        <label className="flex items-center gap-2 p-2.5 rounded-lg border border-emerald-300 bg-white cursor-pointer shadow-2xs">
                                            <input
                                                type="checkbox"
                                                checked={includeRegFee}
                                                onChange={e => setIncludeRegFee(e.target.checked)}
                                                className="rounded text-emerald-600 focus:ring-emerald-500"
                                            />
                                            <span className="text-xs font-bold text-emerald-950">
                                                Frais d'inscription annuels (+ {Number(currentForfait?.registration_fee || 10000).toLocaleString('fr-FR')} FCFA)
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Récapitulatif Financier Encart */}
                                <div className="p-4 bg-white rounded-xl border-2 border-emerald-300 flex items-center justify-between shadow-xs">
                                    <div className="text-xs space-y-0.5 text-gray-600">
                                        <p>Abonnement Taekwondo : <strong className="text-gray-900">{Number(currentForfait?.price || 15000).toLocaleString('fr-FR')} FCFA</strong></p>
                                        <p>Frais d'inscription annuels : <strong className="text-gray-900">{includeRegFee ? `${Number(currentForfait?.registration_fee || 10000).toLocaleString('fr-FR')} FCFA` : '0 FCFA (Non inclus)'}</strong></p>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[11px] font-bold text-gray-500 uppercase block">Total à payer</span>
                                        <span className="text-2xl font-black text-emerald-700">
                                            {totalAmount.toLocaleString('fr-FR')} FCFA
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Section 5 : Autorisation Droit à l'Image & Signature */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs text-gray-700 space-y-2">
                                <label className="flex items-start gap-2.5 cursor-pointer font-medium">
                                    <input
                                        type="checkbox"
                                        checked={formData.photo_authorization}
                                        onChange={e => setFormData({ ...formData, photo_authorization: e.target.checked })}
                                        className="mt-0.5 rounded text-yellow-500 focus:ring-yellow-400"
                                    />
                                    <span>
                                        <strong>Autorisation droit à l'image :</strong> J'autorise HOP FITNESS à utiliser la photo de mon enfant pour son identification et pour assurer sa sécurité au sein de l'établissement.
                                    </span>
                                </label>
                                <p className="text-[11px] text-gray-500 italic">
                                    * En validant ce formulaire, le parent certifie l'exactitude des informations et déclare avoir pris connaissance des conditions d'inscription et des règles de sécurité de HOP FITNESS.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-5 py-2.5 rounded-xl border border-gray-300 font-bold text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2.5 rounded-xl bg-[#eab308] hover:bg-yellow-400 text-black font-extrabold text-sm transition-all shadow-md cursor-pointer"
                                >
                                    {isSubmitting ? 'Génération en cours...' : 'Valider & Générer les Cartes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================================== */}
            {/* MODALE 2 : GÉNÉRATEUR & IMPRESSION DES CARTES OFFICIELLES */}
            {/* ======================================================== */}
            {viewingCardsStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-black/85 backdrop-blur-md overflow-y-auto">
                    <div className="bg-[#121212] border-2 border-[#eab308] rounded-3xl max-w-4xl w-full max-h-[94vh] overflow-y-auto shadow-2xl p-6 text-white relative">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-800 pb-4 mb-6">
                            <div>
                                <span className="text-xs font-black uppercase tracking-wider text-[#eab308] block">
                                    Générateur de Cartes Sécurisées
                                </span>
                                <h3 className="text-2xl font-black text-white">
                                    Badges Taekwondo — {viewingCardsStudent.first_name} {viewingCardsStudent.last_name}
                                </h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePrintWindow}
                                    className="px-4 py-2 bg-[#eab308] text-black font-extrabold text-xs rounded-xl flex items-center gap-1.5 hover:bg-yellow-400 transition-all cursor-pointer"
                                >
                                    <Printer size={16} /> Imprimer les Badges
                                </button>
                                <button
                                    onClick={() => setViewingCardsStudent(null)}
                                    className="p-2 text-gray-400 hover:text-white rounded-full bg-white/10"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Instructions */}
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-xs text-yellow-200 mb-6 flex items-center gap-2">
                            <Sparkles size={16} className="text-[#eab308] shrink-0" />
                            <span>
                                Ces cartes sont calibrées au format badge standard CR80. Le code QR intégré sur le verso est scannable directement à l'accueil pour le pointage des présences et la sécurité des sorties.
                            </span>
                        </div>

                        {/* ======================================= */}
                        {/* 1. CARTE ENFANT (RECTO & VERSO)         */}
                        {/* ======================================= */}
                        <div className="mb-8">
                            <h4 className="text-sm font-black text-[#eab308] uppercase tracking-wider mb-4 flex items-center gap-2">
                                <Award size={16} /> 1. Carte d'Identification Enfant (Recto / Verso)
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
                                {/* RECTO ENFANT - STYLE OFFICIEL HOP FITNESS */}
                                <div className="w-[370px] h-[230px] bg-white rounded-2xl shadow-2xl flex flex-col justify-between relative overflow-hidden select-none border border-gray-200">
                                    {/* Watermark silhouette taekwondo */}
                                    <div className="absolute left-36 top-16 opacity-15 pointer-events-none select-none">
                                        <svg width="110" height="130" viewBox="0 0 100 120" fill="#333">
                                            <path d="M50 15 C54 15 57 12 57 8 C57 4 54 1 50 1 C46 1 43 4 43 8 C43 12 46 15 50 15 Z M47 22 L55 22 L65 38 L88 20 L92 25 L68 46 L70 65 L88 78 L84 84 L65 69 L60 85 L78 115 L70 120 L48 85 L42 62 L32 75 L18 72 L16 66 L30 58 L42 42 Z" />
                                        </svg>
                                    </div>

                                    {/* Photo Enfant positionnée en haut à droite */}
                                    <div className="absolute top-3.5 right-4 w-[92px] h-[116px] rounded-2xl border-2 border-gray-400 overflow-hidden bg-gray-100 shadow-md flex items-center justify-center z-20">
                                        {viewingCardsStudent.photo_url ? (
                                            <img 
                                                src={viewingCardsStudent.photo_url} 
                                                alt="Photo Enfant" 
                                                className="w-full h-full object-cover" 
                                            />
                                        ) : (
                                            <Award size={32} className="text-yellow-600" />
                                        )}
                                    </div>

                                    {/* Header : Logo HopFitness + Titre */}
                                    <div className="flex items-center gap-2 pl-4 pr-[110px] pt-3 pb-1">
                                        <img 
                                            src="/logohop_fitness.jpeg" 
                                            alt="Hop Fitness Logo" 
                                            className="w-14 h-14 rounded-xl object-contain drop-shadow-sm shrink-0" 
                                        />
                                        <div className="text-center flex-1">
                                            <h2 className="text-[17px] font-black italic tracking-wide text-black uppercase leading-tight font-sans">
                                                TAEKWONDO
                                            </h2>
                                            <h3 className="text-xs font-black italic tracking-widest text-black uppercase -mt-0.5 font-sans">
                                                ENFANTS
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Body : Informations calibrées sur la gauche */}
                                    <div className="px-4 z-10 max-w-[245px] pb-2 space-y-1">
                                        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                            <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-24 shrink-0">NOM :</span>
                                            <span className="text-black font-black text-[12px] uppercase truncate">{viewingCardsStudent.last_name}</span>
                                        </div>
                                        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                            <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-24 shrink-0">PRÉNOMS :</span>
                                            <span className="text-black font-black text-[12px] uppercase truncate">{viewingCardsStudent.first_name}</span>
                                        </div>
                                        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                            <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-24 shrink-0">DATE NAISS. :</span>
                                            <span className="text-black font-black text-[11px]">{new Date(viewingCardsStudent.date_of_birth).toLocaleDateString('fr-FR')}</span>
                                        </div>
                                        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                            <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-24 shrink-0">NIVEAU :</span>
                                            <span className="text-black font-black text-[11px] uppercase truncate">{viewingCardsStudent.level}</span>
                                        </div>
                                        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                            <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-24 shrink-0">N° CARTE :</span>
                                            <span className="text-black font-black font-mono text-[11px]">{viewingCardsStudent.card_number}</span>
                                        </div>
                                    </div>

                                    {/* Footer Bandeau Jaune */}
                                    <div className="bg-[#f59e0b] text-black text-center py-1.5 font-black text-[10px] tracking-widest uppercase shadow-inner mt-auto z-10">
                                        DISCIPLINE • CONFIANCE • RESPECT • RÉUSSITE
                                    </div>
                                </div>

                                {/* VERSO ENFANT - ASSORTI */}
                                <div className="w-[370px] h-[230px] bg-white rounded-2xl shadow-2xl flex flex-col justify-between items-center text-center relative overflow-hidden select-none border border-gray-200">
                                    {/* Header */}
                                    <div className="flex items-center gap-3 pt-3.5 px-4">
                                        <img 
                                            src="/logohop_fitness.jpeg" 
                                            alt="Hop Fitness Logo" 
                                            className="w-10 h-10 rounded-lg object-contain" 
                                        />
                                        <span className="font-black text-sm tracking-wider text-black uppercase font-sans">
                                            HOP FITNESS
                                        </span>
                                    </div>

                                    {/* QR Code */}
                                    <div className="bg-white p-2 rounded-2xl shadow-md border-2 border-black/80 my-auto">
                                        <img 
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(viewingCardsStudent.card_number)}`} 
                                            alt="QR Code Enfant"
                                            className="w-24 h-24 object-contain"
                                        />
                                    </div>

                                    <div className="text-[11px] font-mono font-black text-black -mt-1 mb-1">
                                        {viewingCardsStudent.card_number}
                                    </div>

                                    {/* Footer Bandeau Jaune */}
                                    <div className="bg-[#f59e0b] text-black w-full text-center py-2 font-black text-[10px] tracking-widest uppercase shadow-inner">
                                        DÉPASSE TES LIMITES
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ======================================= */}
                        {/* 2. CARTES PARENTS AUTORISÉS             */}
                        {/* ======================================= */}
                        <div>
                            <h4 className="text-sm font-black text-[#eab308] uppercase tracking-wider mb-4 flex items-center gap-2">
                                <ShieldCheck size={16} /> 2. Carte(s) Autorisée(s) Parent / Tuteur (Recto / Verso)
                            </h4>

                            {viewingCardsStudent.pickups && viewingCardsStudent.pickups.length > 0 ? (
                                <div className="space-y-6">
                                    {viewingCardsStudent.pickups.map((pickup, idx) => (
                                        <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
                                            {/* RECTO TUTEUR - STYLE OFFICIEL HOP FITNESS */}
                                            <div className="w-[370px] h-[230px] bg-white rounded-2xl shadow-2xl flex flex-col justify-between relative overflow-hidden select-none border border-gray-200">
                                                {/* Cadre Photo Tuteur positionnée en haut à droite */}
                                                <div className="absolute top-3.5 right-4 w-[92px] h-[116px] rounded-2xl border-2 border-gray-400 overflow-hidden bg-gray-100 shadow-md flex items-center justify-center z-20">
                                                    {pickup.photo_url ? (
                                                        <img src={pickup.photo_url} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <UserCheck size={32} className="text-yellow-600" />
                                                    )}
                                                </div>

                                                {/* Header : Logo HopFitness + Titre */}
                                                <div className="flex items-center gap-2 pl-4 pr-[110px] pt-3 pb-1">
                                                    <img 
                                                        src="/logohop_fitness.jpeg" 
                                                        alt="Hop Fitness Logo" 
                                                        className="w-14 h-14 rounded-xl object-contain drop-shadow-sm shrink-0" 
                                                    />
                                                    <div className="text-center flex-1">
                                                        <h2 className="text-xs font-black tracking-widest text-[#d97706] uppercase leading-tight font-sans">
                                                            CARTE AUTORISÉE
                                                        </h2>
                                                        <h3 className="text-sm font-black italic tracking-wide text-black uppercase font-sans">
                                                            PARENT / TUTEUR
                                                        </h3>
                                                    </div>
                                                </div>

                                                {/* Body : Infos calibrées sur la gauche */}
                                                <div className="px-4 z-10 max-w-[245px] pb-2 space-y-0.5">
                                                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                        <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-20 shrink-0">NOM :</span>
                                                        <span className="text-black font-black text-[12px] uppercase truncate">{pickup.last_name}</span>
                                                    </div>
                                                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                        <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-20 shrink-0">PRÉNOMS :</span>
                                                        <span className="text-black font-black text-[12px] uppercase truncate">{pickup.first_name}</span>
                                                    </div>
                                                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                        <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-20 shrink-0">LIEN :</span>
                                                        <span className="text-yellow-700 font-extrabold uppercase text-[11px] truncate">{pickup.relationship}</span>
                                                    </div>
                                                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                        <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-20 shrink-0">TÉL :</span>
                                                        <span className="text-black font-black text-[11px]">{pickup.phone}</span>
                                                    </div>
                                                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                        <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-20 shrink-0">ENFANT :</span>
                                                        <span className="text-black font-black text-[11px] truncate">{viewingCardsStudent.first_name} {viewingCardsStudent.last_name}</span>
                                                    </div>
                                                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                                        <span className="text-gray-700 text-[10px] font-black uppercase tracking-wide w-20 shrink-0">N° CARTE :</span>
                                                        <span className="text-black font-black font-mono text-[11px]">{pickup.card_number || 'PT-...'}</span>
                                                    </div>
                                                </div>

                                                {/* Footer Bandeau Jaune */}
                                                <div className="bg-[#f59e0b] text-black text-center py-1.5 font-black text-[10px] tracking-widest uppercase shadow-inner mt-auto z-10">
                                                    SÉCURITÉ • CONFIANCE • RESPONSABILITÉ
                                                </div>
                                            </div>

                                            {/* VERSO TUTEUR - ASSORTI */}
                                            <div className="w-[370px] h-[230px] bg-white rounded-2xl shadow-2xl flex flex-col justify-between items-center text-center relative overflow-hidden select-none border border-gray-200">
                                                {/* Header */}
                                                <div className="flex items-center gap-2 pt-3 px-4">
                                                    <img 
                                                        src="/logohop_fitness.jpeg" 
                                                        alt="Hop Fitness Logo" 
                                                        className="w-8 h-8 rounded-lg object-contain" 
                                                    />
                                                    <span className="text-xs font-black tracking-wider text-black uppercase font-sans">
                                                        HOP FITNESS — HOUSE OF POWER
                                                    </span>
                                                </div>

                                                <p className="text-[10px] text-gray-700 leading-snug px-6 font-semibold">
                                                    Cette carte permet de récupérer l'enfant inscrit au Taekwondo uniquement sur présentation de cette carte.
                                                </p>

                                                {/* QR Code */}
                                                <div className="bg-white p-2 rounded-2xl shadow-md border-2 border-black/80">
                                                    <img 
                                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(pickup.card_number || 'PT-UNKNOWN')}`} 
                                                        alt="QR Code Tuteur"
                                                        className="w-20 h-20 object-contain"
                                                    />
                                                </div>

                                                <div className="text-[11px] font-mono font-black text-black -mt-1 mb-1">
                                                    {pickup.card_number}
                                                </div>

                                                {/* Footer Bandeau Jaune */}
                                                <div className="bg-[#f59e0b] text-black w-full text-center py-2 font-black text-[10px] tracking-widest uppercase shadow-inner">
                                                    MERCI DE VOTRE COLLABORATION
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 italic">Aucune personne autorisée.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ================================================================= */}
            {/* MODALE 3 : FICHE OFFICIELLE D'INSCRIPTION & AUTORISATION (A4 PDF) */}
            {/* ================================================================= */}
            {viewingContractStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-black/85 backdrop-blur-md overflow-y-auto">
                    <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[94vh] overflow-y-auto shadow-2xl p-6 md:p-10 text-gray-900 relative">
                        {/* Header bar */}
                        <div className="flex items-center justify-between border-b pb-4 mb-6">
                            <span className="text-xs font-black uppercase text-yellow-600 tracking-wider">
                                Document Officiel Hop Fitness
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePrintWindow}
                                    className="px-4 py-2 bg-black text-yellow-400 font-extrabold text-xs rounded-xl flex items-center gap-1.5 hover:bg-gray-800 transition-all cursor-pointer"
                                >
                                    <Printer size={16} /> Imprimer la Fiche (A4)
                                </button>
                                <button
                                    onClick={() => setViewingContractStudent(null)}
                                    className="p-2 text-gray-400 hover:text-gray-700 rounded-full bg-gray-100"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Document Content */}
                        <div className="space-y-6 text-sm text-gray-800 leading-relaxed font-serif border border-gray-200 p-6 md:p-8 rounded-2xl bg-white shadow-sm">
                            {/* Logo & Titre */}
                            <div className="text-center border-b-2 border-yellow-500 pb-4">
                                <h1 className="text-2xl md:text-3xl font-black font-sans uppercase tracking-tight text-gray-900">
                                    HOP FITNESS
                                </h1>
                                <h2 className="text-lg md:text-xl font-bold font-sans uppercase text-yellow-600 mt-1">
                                    CONDITIONS D’INSCRIPTION — TAEKWONDO ENFANTS
                                </h2>
                                <p className="text-xs font-sans text-gray-500 mt-1">
                                    Riviera Palmeraie, Carrefour Guiraud — Abidjan, Côte d'Ivoire | Tél : 01 01 58 50 53 / 07 13 47 07 99
                                </p>
                            </div>

                            {/* Conditions officielles */}
                            <div className="space-y-2 text-xs md:text-sm">
                                <ul className="list-disc pl-5 space-y-1.5 text-gray-700 font-sans">
                                    <li>L'inscription doit être effectuée par le parent ou le tuteur responsable de l'enfant.</li>
                                    <li>Les informations fournies doivent être exactes, complètes et à jour.</li>
                                    <li>Une photo récente de l'enfant est obligatoire pour établir sa carte d'identification.</li>
                                    <li>Le parent/tuteur doit fournir les coordonnées nécessaires afin de pouvoir être contacté en cas d'urgence.</li>
                                    <li>Le parent/tuteur doit déclarer les personnes autorisées à récupérer l'enfant après les cours.</li>
                                    <li>Chaque personne autorisée doit fournir ses informations ainsi qu'une photo d'identification avant de pouvoir récupérer l'enfant.</li>
                                    <li><strong>Aucun enfant ne sera remis à une personne non enregistrée dans le système</strong>, sauf autorisation exceptionnelle vérifiée directement auprès du responsable légal et selon la procédure de HOP FITNESS.</li>
                                    <li>La carte de la personne autorisée doit être présentée lors de chaque récupération de l'enfant.</li>
                                    <li>En cas de perte, de vol ou de détérioration de la carte de l'enfant ou de la carte du parent/tuteur, une réclamation doit obligatoirement être effectuée auprès de HOP FITNESS afin que la carte puisse être désactivée. <strong>L'établissement d'une nouvelle carte est soumis au paiement de 10.000 F CFA.</strong></li>
                                    <li>Une carte désactivée, expirée, perdue ou remplacée ne peut plus être utilisée pour récupérer l'enfant.</li>
                                    <li>Le parent/tuteur doit informer HOP FITNESS de tout changement concernant les personnes autorisées.</li>
                                    <li>HOP FITNESS se réserve le droit de demander une vérification supplémentaire en cas de doute sur l'identité de la personne venant chercher l'enfant.</li>
                                    <li>Le présent système de contrôle est mis en place avant tout afin de garantir la sécurité et la protection de l'enfant.</li>
                                </ul>
                            </div>

                            {/* Autorisation du Parent */}
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 font-sans space-y-3">
                                <h3 className="font-black text-sm uppercase text-gray-900 border-b pb-2">
                                    AUTORISATION DU PARENT / TUTEUR
                                </h3>
                                <p className="text-xs">
                                    Je soussigné(e), Nom et Prénoms du parent/tuteur : <span className="font-black text-gray-900 underline">{viewingContractStudent.parent_name}</span>,
                                    <br />
                                    Responsable légal de l'enfant : <span className="font-black text-gray-900 underline">{viewingContractStudent.first_name} {viewingContractStudent.last_name}</span> (Né(e) le {new Date(viewingContractStudent.date_of_birth).toLocaleDateString('fr-FR')}), N° Badge : <span className="font-mono font-bold">{viewingContractStudent.card_number}</span>,
                                </p>
                                <p className="text-xs">
                                    certifie l'exactitude des informations fournies lors de l'inscription.
                                    <br />
                                    Je reconnais avoir pris connaissance des conditions d'inscription et du système de contrôle mis en place par HOP FITNESS.
                                    <br />
                                    J'autorise uniquement les personnes enregistrées et validées dans le système à récupérer mon enfant après ses cours de Taekwondo.
                                    <br />
                                    Je m'engage à informer immédiatement HOP FITNESS de tout changement concernant les personnes autorisées, ainsi que de toute perte ou utilisation frauduleuse d'une carte.
                                </p>
                                <div className="text-xs font-bold text-gray-800 flex items-center gap-2 pt-1">
                                    <span className="w-4 h-4 rounded bg-yellow-500 text-black flex items-center justify-center text-[10px]">✓</span>
                                    <span>J'autorise HOP FITNESS à utiliser la photo de mon enfant pour son identification et pour assurer sa sécurité au sein de l'établissement.</span>
                                </div>
                            </div>

                            {/* Personnes autorisées déclarées */}
                            <div className="font-sans">
                                <h4 className="text-xs font-black uppercase text-gray-700 mb-2">
                                    Personnes autorisées déclarées :
                                </h4>
                                <table className="w-full text-xs text-left border border-gray-200 rounded-lg overflow-hidden">
                                    <thead className="bg-gray-100 font-bold">
                                        <tr>
                                            <th className="p-2 border">Nom & Prénoms</th>
                                            <th className="p-2 border">Lien avec l'enfant</th>
                                            <th className="p-2 border">Téléphone</th>
                                            <th className="p-2 border">N° Carte Attribué</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {viewingContractStudent.pickups?.map((p, i) => (
                                            <tr key={i} className="border-t">
                                                <td className="p-2 border font-bold">{p.first_name} {p.last_name}</td>
                                                <td className="p-2 border">{p.relationship}</td>
                                                <td className="p-2 border">{p.phone}</td>
                                                <td className="p-2 border font-mono font-bold text-yellow-700">{p.card_number}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Signatures et Validation */}
                            <div className="font-sans pt-4 border-t-2 border-gray-200">
                                <h3 className="font-black text-sm uppercase text-gray-900 mb-4 text-center">
                                    SIGNATURES ET VALIDATION
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
                                    {/* Signature Parent */}
                                    <div className="border border-gray-300 p-3 rounded-lg flex flex-col justify-between h-40">
                                        <div>
                                            <span className="font-black block uppercase text-gray-800">Signature du parent / tuteur</span>
                                            <p className="text-[11px] text-gray-600 mt-1">Nom : <strong>{viewingContractStudent.parent_name}</strong></p>
                                            <p className="text-[11px] text-gray-600">Date : <strong>{new Date().toLocaleDateString('fr-FR')}</strong></p>
                                        </div>
                                        <div className="border-t border-dashed border-gray-400 pt-1 text-[10px] text-gray-400 text-center">
                                            Signature
                                        </div>
                                    </div>

                                    {/* Signature Responsable */}
                                    <div className="border border-gray-300 p-3 rounded-lg flex flex-col justify-between h-40">
                                        <div>
                                            <span className="font-black block uppercase text-gray-800">Signature responsable HOP FITNESS</span>
                                            <p className="text-[11px] text-gray-600 mt-1">Nom : <strong>Direction HopFitness</strong></p>
                                            <p className="text-[11px] text-gray-600">Date : <strong>{new Date().toLocaleDateString('fr-FR')}</strong></p>
                                        </div>
                                        <div className="border-t border-dashed border-gray-400 pt-1 text-[10px] text-gray-400 text-center">
                                            Signature
                                        </div>
                                    </div>

                                    {/* Cachet officiel */}
                                    <div className="border-2 border-yellow-500/60 p-3 rounded-lg flex flex-col justify-between h-40 bg-yellow-50/20">
                                        <div>
                                            <span className="font-black block uppercase text-yellow-800">CACHET OFFICIEL HOP FITNESS</span>
                                            <p className="text-[10px] text-gray-500 mt-1">Cachet de l'établissement</p>
                                        </div>
                                        <div className="border border-dashed border-yellow-400 h-20 rounded flex items-center justify-center text-[10px] text-yellow-700 font-bold uppercase">
                                            [ EMPREINTE DU CACHET ]
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bas de page officiel */}
                            <div className="text-center font-sans pt-4 border-t border-gray-200">
                                <p className="font-black text-sm uppercase tracking-wider text-gray-900">
                                    HOP FITNESS — Riviera Palmeraie
                                </p>
                                <p className="font-extrabold text-xs text-yellow-600 uppercase tracking-widest mt-0.5">
                                    DÉPASSE TES LIMITES !
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ======================================================== */}
            {/* MODALE 4 : RÉABONNEMENT ÉLÈVE TAEKWONDO                   */}
            {/* ======================================================== */}
            {reaboStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full border border-gray-100 p-6 animate-in fade-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 bg-yellow-100 text-yellow-800 rounded-xl">
                                    <RotateCw size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-gray-900">
                                        Réabonnement Taekwondo
                                    </h3>
                                    <p className="text-xs text-gray-500">
                                        Élève : <strong>{reaboStudent.first_name} {reaboStudent.last_name}</strong> ({reaboStudent.card_number})
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setReaboStudent(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitReabo} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                    Forfait Taekwondo *
                                </label>
                                <select
                                    value={reaboForfaitId}
                                    onChange={e => setReaboForfaitId(Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#eab308]"
                                >
                                    {forfaits.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name} — {Number(f.price).toLocaleString('fr-FR')} FCFA / {f.duration || f.periodicity}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                    Date de début *
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={reaboStartDate}
                                    onChange={e => setReaboStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#eab308]"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                    Mode de paiement *
                                </label>
                                <select
                                    value={reaboPaymentMethod}
                                    onChange={e => setReaboPaymentMethod(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#eab308]"
                                >
                                    <option value="especes">Espèces</option>
                                    <option value="wave">Wave</option>
                                    <option value="orange_money">Orange Money</option>
                                    <option value="mtn_money">MTN Money</option>
                                    <option value="moov_money">Moov Money</option>
                                    <option value="carte">Carte Bancaire</option>
                                    <option value="virement">Virement</option>
                                </select>
                            </div>

                            <label className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={reaboIncludeRegFee}
                                    onChange={e => setReaboIncludeRegFee(e.target.checked)}
                                    className="rounded text-yellow-600 focus:ring-yellow-500"
                                />
                                <span className="text-xs font-bold text-gray-800">
                                    Renouvellement annuel : Inclure les frais d'inscription (+ 10 000 FCFA)
                                </span>
                            </label>

                            {/* Total récap */}
                            {(() => {
                                const currentF = forfaits.find(f => f.id === reaboForfaitId) || forfaits[0];
                                const bPrice = currentF ? parseFloat(currentF.price) : 15000;
                                const rFee = reaboIncludeRegFee && currentF ? parseFloat(currentF.registration_fee || 0) : 0;
                                const tot = bPrice + rFee;
                                return (
                                    <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200 flex items-center justify-between">
                                        <span className="text-xs font-bold text-gray-700">Total à régler :</span>
                                        <span className="text-xl font-black text-brand-dark">
                                            {tot.toLocaleString('fr-FR')} FCFA
                                        </span>
                                    </div>
                                );
                            })()}

                            <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setReaboStudent(null)}
                                    className="px-4 py-2 border border-gray-300 font-bold text-xs text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={reaboSubmitting}
                                    className="px-6 py-2 bg-[#eab308] hover:bg-yellow-400 text-black font-black text-xs rounded-lg shadow-sm transition-all disabled:opacity-50"
                                >
                                    {reaboSubmitting ? 'Validation...' : 'Valider le réabonnement'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reçu imprimable officiel */}
            <ReceiptModal
                isOpen={isReceiptOpen}
                onClose={() => setIsReceiptOpen(false)}
                type="subscription"
                data={receiptData || {}}
            />
        </div>
    );
};

export default TaekwondoKids;
